<template>
    <div class="account-page-padding">
        <div class="lists">
            <div class="header">
                <h1>{{ list.name ? list.name : "Un-named List" }}</h1>
            </div>
            <div class="search-fields">
                <div style="align-items: center;">
                    <h4>
                        {{ `${list?.total_quantity ?? 0} item${list.total_quantity != 1 ? 's' : ''}` }} &nbsp; 
                        {{ `${list?.total_skus ?? 0} sku${list.total_skus != 1 ? 's' : ''}` }} &nbsp; 
                        {{ canSeePricing ? `$${parseFloat(list.total ?? 0).toFixed(2)}` : '' }}
                    </h4>
                </div>
                <div class="buttons">
                    <RothcoCta id="rename_list_button" :button_icon="null" :inertia="false" class="secondary" @click.prevent="openRenameList()">
                        Rename List
                    </RothcoCta>
                    <RothcoCta v-if="list.list_id != current_list?.list_id"
                        id="make_current_button"
                        :button_icon="null" :inertia="false" class="secondary" 
                        @click.prevent="makeCurrentList(list, {preserveState: true, preserveScroll: true})">
                        Make Current
                    </RothcoCta>
                    <RothcoCta v-else
                        id="is_current_button"
                        :button_icon="null" :inertia="false" class="disabled" disabled>
                        Current List
                    </RothcoCta>
                    <RothcoCta id="delete_list_button" :button_icon="null" :inertia="false" class="secondary" @click.prevent="openDeleteList()">
                        Delete List
                    </RothcoCta>
                    <div class="selectore-state select-wrapper" style="flex-grow: unset;">
                        <select v-model="limit" name="limit" id="per_page_filter">
                            <option value="" selected hidden>Items Per Page</option>
                            <option value="5">5</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </select>
                        <label for="per_page_filter" class="select-label">
                            <p class="paragraph-default">Items Per Page</p>
                        </label>
                    </div>
                </div>
            </div>
        </div>
        <rothco-table
            :key="`table_key_${tableKey}`" id="list_items_table"
            class="list_items_table"
            v-model:focused="focused"
            v-model:content="tableContent"
            :visible-fields="visibleFields"
            primary-key="list_item_id"
            :backendPagination="backendPagination"
            :useBackendSorting="true"
            focusable
        >
            <template v-for="(row, index) in tableContent" :key="index" v-slot:[`item_name_${index}`]="slotProps">
                <div class="list_item">
                    <Link as="a" :href="route('productDetail.show',[row.data.seo_name])">
                        <img :src="row.data.item_image" :srcset="row.data.item_image_srcset" :sizes="row.data.item_image_sizes"/>
                    </Link>
                    <div class="item_description">
                        <RothcoCta ctaType="link" :href="route('productDetail.show',[row.data.seo_name])">
                            {{ row.data.item_name }}
                        </RothcoCta>
                        <div class="badge-wrapper">
                            <p v-for="(variantValue,i) in (row.data?.item_variation ?? [])" :key="i" class="paragraph-default badge">
                                {{ variantValue }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <template v-for="(row, index) in tableContent" :key="index" v-slot:[`delete_${index}`]="slotProps">
                <button class="secondary small" @click="openDeleteListItem(row.data)">
                    <DeleteBlackIcon class="secondary" style="margin:0;"/>
                </button>
            </template>
            <template v-for="(row, index) in tableContent" :key="index" v-slot:[`price_${index}`]="slotProps">
                <p class="text-right">${{ parseFloat(row.data.price ?? 0).toFixed(2) }}</p>
            </template>
            <template v-for="(row, index) in tableContent" :key="index" v-slot:[`subtotal_${index}`]="slotProps">
                <p class="text-right">${{ parseFloat(row.data.subtotal ?? 0).toFixed(2) }}</p>
            </template>
            <template v-for="(row, index) in tableContent" :key="index" v-slot:[`quantity_${index}`]="slotProps">
                <div class="inline-flex flex-col items-center">
                    <div class="flex border border-neutral-300 rounded-md">
                        <number-input
                            :id="`desktop_quantity_input_${index}`"
                            v-model.natural.whole="row.data.quantity" 
                            type="number"
                            class="appearance-none mx-2 w-8 text-center bg-transparent font-medium [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:display-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:display-none [&::-webkit-outer-spin-button]:m-0 [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none disabled:placeholder-disabled-900 focus-visible:outline focus-visible:outline-offset focus-visible:rounded-sm"
                            min="0" 
                            max="99999" 
                            step="1" 
                            @focus="row.data._oldQty = row.data.quantity"
                            @blur="handleQuantityInput(row.data, $event, index)"
                            @wheel.prevent="blurOnWheel"
                        />
                    </div>
                </div>
            </template>
        </rothco-table>
    </div>
</template>
<script lang="ts" setup>
    import { ref, toRefs, computed, watch, onMounted, type PropType, inject } from "vue";
    import { router, Link } from "@inertiajs/vue3";
    import { useRouter } from "@/helpers/composables/useRouter";
    import { useLists } from "@/helpers/composables/useList";
    import { useDialog } from "primevue/usedialog";

    import Account from "@/Shared/Account.vue";
    import RothcoTable from "@/components/RothcoTable.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import RenameListModal from "@/components/RenameListModal.vue";
    import ListDeleteModal from "@/components/ListDeleteModal.vue";
    import { formatTotalPrice } from "@/helpers/composables/usePriceFormatting"
    import DeleteBlackIcon from "@icons/delete-black.svg";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages"
    import {
        productUploadImageResponsiveFromFilename,
        PRODUCT_IMAGE_SIZES_LIST_ROW
    } from "@/helpers/productUploadImageUrl";
    
    let route = inject("route");
    let { makeCurrentList, updateListItem } = useLists({ router, route });
    let settings = useSettingsStore();
    const { canSeePricing } = storeToRefs(settings);
    interface List {
        list_id: number;
        name?: string;
        total?: number;
        amonut?: number;
    }

    let props = defineProps({
        list: {
            type: Object as PropType<List>,
            default(){
                return {};
            }
        },
        list_items: {
            type: Object,
            default: () => ({
                data: [],
                meta: {
                    current_page: 1,
                    last_page: 1,
                    per_page: 1,
                    total: 1
                }
            })
        },
        current_list: {
            type: Object as PropType<List>,
            default(){
                return {};
            }
        },
    });

    const { list, list_items } = toRefs(props);

    const limit = ref("5");

    const focused = ref(false);

    let visibleFields = computed(()=>{
        return [
            { 
                label: "Item", 
                field: "item_name", 
                is_custom: true,
                sortable: true,
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'mob-w-75 text-left', 'data-label': "Item" },
                } 
            },
            { 
                label: "Item #", 
                field: "item_no",
                sortable: false,
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'mob-w-25 text-center', 'data-label': "Item #" },
                    bodyCellContent: { class: 'paragraph-default mob-text-center desk-text-center'}
                } 
            },
            { 
                label: "Price", 
                field: "price",
                is_custom: true,
                sortable: true,
                visible: canSeePricing.value,
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCell: { class: 'mob-w-25 text-right', 'data-label': "Price" },
                    bodyCellContent: { class: 'paragraph-default mob-text-left desk-text-right'}
                }
            },
            { 
                label: "Qty", 
                field: "quantity", 
                is_custom: true, 
                sortable: true,
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCell: { class: 'mob-w-25', 'data-label': "Qty" },
                }
            },
            { 
                label: "Subtotal", 
                field: "subtotal",
                is_custom: true, 
                sortable: true,
                visible: canSeePricing.value,
                pt: {
                    headerCell: { class: 'title-xs text-right' },
                    bodyCell: { class: 'mob-w-25 text-right', 'data-label': "Subtotal" },
                    bodyCellContent: { class: 'paragraph-default mob-text-left desk-text-right'}
                }
            },
            { 
                label: "Delete", 
                field: "delete", 
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-center' },
                    bodyCell: { class: 'mob-w-25 text-right', 'data-label': "Delete" },
                }
            }
        ];
    })

    let tableContent = computed(() => {
        return list_items.value.data.map((listItem) => {
            listItem.price = listItem.unit_price;
            const price = (+listItem.price).toFixed(2);
            const subtotal = formatTotalPrice(listItem.price, +listItem.quantity);
            listItem.formattedPrice = `${listItem.currency} ${listItem.currencySymbol}${price}`;
            listItem.formattedSubtotal = `${listItem.currency} ${listItem.currencySymbol}${subtotal}`;
            const fn = listItem.variation?.primary_image?.image_filename;
            const img = fn
                ? productUploadImageResponsiveFromFilename(fn, PRODUCT_IMAGE_SIZES_LIST_ROW)
                : null;
            return {
                data: {
                    ...listItem,
                    item_no: listItem.variation.rothcoItem.rothco_item_no,
                    item_name: listItem.variation.item.name,
                    item_image: img ? img.src : comingSoonImages().small,
                    item_image_srcset: img?.srcSet,
                    item_image_sizes: img?.sizes,
                    item_variation: Object.keys(listItem.variation?.text_description ?? {}).map(key=>{
                        return `${key}: ${listItem.variation.text_description[key]}`
                    }),
                    seo_name: listItem.variation.item.seo_name 
                }
            }
        })
    });

    const backendPagination = computed(()=>{
        return {
            ...props.list_items.links,
            ...props.list_items.meta,
        }
    })

    let dialog = useDialog();

    let openRenameList = function () {
        dialog.open(RenameListModal, {
            data: {
                list_id: list.value.list_id,
                list_name: list.value.name,
                beforeClose: reloadList
            }
        })
    };

    let openDeleteList = () => {
        dialog.open(ListDeleteModal, {
            data: {
                type:"list",
                list_id: list.value.list_id,
                beforeCloseForList: () => router.visit(route("account.lists"))
            }
        });
    };

    let openDeleteListItem = (listItem) => {
        dialog.open(ListDeleteModal, {
            data: {
                type:"item",
                list_item_id: listItem.list_item_id,
                beforeCloseForItem: reloadList
            }
        });
    }

    let reloadList = () => {
        router.reload({only: ['list','list_items','current_list']});
    };

    let { to } = useRouter({
        router,
        route
    });

    onMounted(() => {
        let params = new URLSearchParams(window.location.search);
        params.forEach((value, key) => {
            if (key == "limit") {
                limit.value = value;
            }
        });
    });

    watch(limit, (newVal)=>{
        let params = new URLSearchParams(window.location.search);
        if((params.has("limit") && params.get("limit") != newVal) || !params.has("limit")) {
            to({ newParams: { limit: newVal, page: 1 }, options: {preserveState: true, preserveScroll: true}});
        }
    })

    let tableKey = ref(0);

    let handleQuantityInput = function (row, event: Event, index) {
        let oldVal = row._oldQty;
        let newVal: number | null = +(event.target as HTMLInputElement)?.value;
        if (newVal < 1) {
            tableKey.value++;
            return;
        }
        newVal = newVal ? parseInt(newVal.toString()) : null;
        list_items.value.data[index].quantity = newVal;
        if (newVal != oldVal) {
            updateListItem(list_items.value.data[index],{
                afterFetch: (ctx)=>{
                    list_items.value.data[index].unit_price = ctx.data.unit_price;
                    list_items.value.data[index].subtotal = ctx.data.subtotal;
                    router.reload({only: ['list','current_list']});
                }
            });
        }
    };

    function blurOnWheel(event) {
        event.target.blur();
    }
</script>
